import { PlayArrow } from '@mui/icons-material';
import { Box, Button, Drawer, Grid, IconButton, InputAdornment, TextField, Theme, Typography } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ArchitectureDrawerProps } from '../../../@types/ContextProviderRelated';
import { EElementType, ESubMenu, EUnitType } from '../../../@types/Enums';
import { EPendingType } from '../../../@types/PendingItemTypes';
import { IconElementCircle, IconElementRectangle, IconElementTriangle, IconTriangleAngle, IconTriangleSideA, IconTriangleSideB, IconTriangleSideC } from '../../../assets/icons';
import { useBuilding } from '../../../provider/BuildingProvider/BuildingProvider';
import { usePendingItem } from '../../../provider/PendingItemProvider';
import { useToolbarContext } from '../../../provider/ToolbarProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import { immersightBlue } from '../../../utils/colors';
import { MM_TO_INTERNAL_FACTOR } from '../../../utils/constants';
import { handleWheelInput, WheelInputConfig } from '../../../utils/handleMouseEvents';
import ImsDoubleRectButton from '../ImsDoubleRectButton';

const CreateElementsDrawer = (props: ArchitectureDrawerProps): JSX.Element => {
  const { id, open, onClose } = props;
  const { setCurrentlyActiveSubMenu } = useToolbarContext();
  const { addElement } = useBuilding();
  const { startPlacement } = usePendingItem();
  const { unitType } = useUnitContext();
  const intl = useIntl();

  const mmLocalizedLabel = intl.formatMessage({ id: 'unit.millimeter', defaultMessage: 'mm' });
  const inLocalizedLabel = intl.formatMessage({ id: 'unit.inch', defaultMessage: 'in' });

  const minMetric = 30;
  const maxMetric = 10000;
  const minInches = Math.max(1, Math.round(minMetric / 25.4));
  const maxInches = Math.round(maxMetric / 25.4);

  const defaultDimension = unitType === EUnitType.METRIC ? 1000 : Math.round(1000 / 25.4);

  const [selectedShape, setSelectedShape] = useState<EElementType | null>(null);
  const [rectangle, setRectangle] = useState({ width: String(defaultDimension), length: String(defaultDimension) });
  const [circle, setCircle] = useState({ width: String(defaultDimension), length: String(defaultDimension) });

  // Triangle State
  const [triangle, setTriangle] = useState({
    sideA: String(defaultDimension),
    sideB: String(defaultDimension),
    sideC: '',
    angle: '90',
  });

  const [error, setError] = useState<string | null>(null);
  const wheelConfig: WheelInputConfig = { unitType, minMetric, maxMetric, minInches, maxInches };

  useEffect(() => {
    if (selectedShape === EElementType.TRIANGLE && !triangle.sideC) {
      const a = parseFloat(triangle.sideA);
      const b = parseFloat(triangle.sideB);
      if (!isNaN(a) && !isNaN(b)) {
        const c = Math.sqrt(a * a + b * b);
        setTriangle((prev) => ({ ...prev, sideC: unitType === EUnitType.METRIC ? c.toFixed(0) : c.toFixed(1) }));
      }
    }
  }, [selectedShape, unitType, triangle.sideA, triangle.sideB, triangle.sideC]);

  const handleCloseDrawer = (): void => {
    setCurrentlyActiveSubMenu(ESubMenu.NONE);
    onClose();
  };

  const handleShapeSelection = (shape: EElementType) => {
    setSelectedShape(selectedShape === shape ? null : shape);
    setError(null);
  };

  const updateTriangle = (field: 'sideA' | 'sideB' | 'sideC', value: string) => {
    const valNum = parseFloat(value);

    if (isNaN(valNum)) {
      setTriangle((prev) => ({ ...prev, [field]: value }));
      return;
    }

    const currentState = { ...triangle, [field]: value };
    const a = parseFloat(currentState.sideA);
    const b = parseFloat(currentState.sideB);
    const c = parseFloat(currentState.sideC);
    const angleRad = (parseFloat(currentState.angle) * Math.PI) / 180;

    if (field === 'sideA' || field === 'sideB') {
      if (!isNaN(a) && !isNaN(b)) {
        const newC = Math.sqrt(Math.pow(a, 2) + Math.pow(b, 2) - 2 * a * b * Math.cos(angleRad));
        currentState.sideC = unitType === EUnitType.METRIC ? newC.toFixed(0) : newC.toFixed(1);
      }
    } else if (field === 'sideC') {
      if (!isNaN(a) && !isNaN(b) && !isNaN(c)) {
        if (a + b <= c || a + c <= b || b + c <= a) {
          setError(intl.formatMessage({ id: 'CreateElementsDrawer.error.impossibleTriangle', defaultMessage: 'Triangle impossible' }));
        } else {
          setError(null);
          const cosGamma = (Math.pow(a, 2) + Math.pow(b, 2) - Math.pow(c, 2)) / (2 * a * b);
          const clampedCos = Math.max(-1, Math.min(1, cosGamma));
          const newAngle = (Math.acos(clampedCos) * 180) / Math.PI;
          currentState.angle = newAngle.toFixed(1);
        }
      }
    }
    setTriangle(currentState);
  };

  const numberInputProps = {
    inputProps: {
      min: unitType === EUnitType.METRIC ? minMetric : minInches,
      max: unitType === EUnitType.METRIC ? maxMetric : maxInches,
      type: 'number',
    },
  };

  const handleInsert = () => {
    let width: number;
    let length: number;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let extraData: any = {};

    if (selectedShape === EElementType.RECTANGLE) {
      width = parseFloat(rectangle.width);
      length = parseFloat(rectangle.length);
    } else if (selectedShape === EElementType.CIRCLE) {
      width = parseFloat(circle.width);
      length = parseFloat(circle.length);
    } else if (selectedShape === EElementType.TRIANGLE) {
      length = parseFloat(triangle.sideA);
      width = parseFloat(triangle.sideB);
      extraData = {
        angle: parseFloat(triangle.angle),
        sideC: parseFloat(triangle.sideC),
      };
    } else {
      setError('Please select a shape.');
      return;
    }

    const currentMin = unitType === EUnitType.METRIC ? minMetric : minInches;
    const currentMax = unitType === EUnitType.METRIC ? maxMetric : maxInches;
    const unitLabel = unitType === EUnitType.METRIC ? mmLocalizedLabel : inLocalizedLabel;

    if (isNaN(width) || isNaN(length) || width < currentMin || width > currentMax || length < currentMin || length > currentMax) {
      setError(intl.formatMessage({ id: 'CreateElementsDrawer.error.invalidSize' }, { min: currentMin, max: currentMax, unit: unitLabel }));
      return;
    }

    if (selectedShape === EElementType.TRIANGLE && error) return;

    let internalWidth = 0;
    let internalLength = 0;
    let internalSideC = 0;
    let internalAngle = 0;

    const factor = unitType === EUnitType.METRIC ? MM_TO_INTERNAL_FACTOR : MM_TO_INTERNAL_FACTOR * 25.4;

    internalLength = length * factor;
    internalWidth = width * factor;
    
    if (selectedShape === EElementType.TRIANGLE) {
        if (extraData.sideC) {
            internalSideC = extraData.sideC * factor;
        }
        if (extraData.angle) {
          internalAngle = (extraData.angle * Math.PI) / 180;        
        }
    }

    // Prepare the base data object
    const pendingData: any = {
      type: selectedShape!,
      width: internalWidth,
      length: internalLength,
      rotation: 0,
    };

    // Only add specific triangle properties if it is a triangle
    if (selectedShape === EElementType.TRIANGLE) {
      pendingData.sideC = internalSideC;
      pendingData.triangleAngle = internalAngle; // Pass angle (in Radians) to ghost
    }

    startPlacement({
      type: EPendingType.ELEMENT,
      data: pendingData,
      onConfirm: (position, spaceUuid) => {
        addElement(
          selectedShape!,
          internalLength,
          internalWidth,
          position,
          spaceUuid,
          [],
          internalSideC, 
          selectedShape === EElementType.TRIANGLE ? internalAngle : undefined 
        );
      },
    });

    handleCloseDrawer();
  };

  const drawerSx: SystemStyleObject<Theme> = {
    '& .MuiPaper-root': {
      top: '64px',
      zIndex: 1000,
      width: '340px',
      height: 'calc(100% - 64px)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
  };

  const iconWrapperSx = {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      fontSize: 20, 
      width: '0.9em',
      height: '0.9em',
      fill: 'none', 
      stroke: '#9B9B9B',  
    }
  };

  return (
    <Drawer key={id} anchor="right" open={open} variant={open ? 'permanent' : 'temporary'} sx={drawerSx}>
      <Grid container direction="column" p={0}>
        <Grid
          item
          sx={{
            backgroundColor: immersightBlue,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 0.5,
          }}
        >
          <Box sx={{ marginLeft: '16px' }}>
            <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
              <FormattedMessage id="CreateElementsDrawer.header" />
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDrawer} sx={{ color: '#fff', '&:hover': { backgroundColor: 'transparent' } }}>
            <PlayArrow sx={{ fontSize: 30 }} />
          </IconButton>
        </Grid>
        <Grid container item direction="column" spacing={4} p={2} flexGrow={1}>
          {/* RECTANGLE */}
          <Grid item>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <ImsDoubleRectButton
                  icon={<IconElementRectangle />}
                  selected={selectedShape === EElementType.RECTANGLE}
                  onClick={() => handleShapeSelection(EElementType.RECTANGLE)}
                />
              </Grid>
              <Grid item xs>
                {selectedShape === EElementType.RECTANGLE ? (
                  <Grid container spacing={1} direction="column">
                    <Grid item>
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={8}>
                          <TextField
                            label={<FormattedMessage id="CreateElementsDrawer.width" />}
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={rectangle.width}
                            onChange={(e) => setRectangle((prev) => ({ ...prev, width: e.target.value }))}
                            onWheel={(e) => handleWheelInput(e, rectangle.width, (v) => setRectangle({ ...rectangle, width: v }), wheelConfig)}
                            {...numberInputProps}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2">{unitType === EUnitType.METRIC ? mmLocalizedLabel : inLocalizedLabel}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={8}>
                          <TextField
                            label={<FormattedMessage id="CreateElementsDrawer.length" />}
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={rectangle.length}
                            onChange={(e) => setRectangle((prev) => ({ ...prev, length: e.target.value }))}
                            onWheel={(e) => handleWheelInput(e, rectangle.length, (v) => setRectangle({ ...rectangle, length: v }), wheelConfig)}
                            {...numberInputProps}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2">{unitType === EUnitType.METRIC ? mmLocalizedLabel : inLocalizedLabel}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body1" fontWeight="bold" color="#9B9B9B">
                    <FormattedMessage id="CreateElementsDrawer.rectangle" />
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* CIRCLE */}
          <Grid item>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <ImsDoubleRectButton icon={<IconElementCircle />} selected={selectedShape === EElementType.CIRCLE} onClick={() => handleShapeSelection(EElementType.CIRCLE)} />
              </Grid>
              <Grid item xs>
                {selectedShape === EElementType.CIRCLE ? (
                  <Grid container spacing={1} direction="column">
                    <Grid item>
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={8}>
                          <TextField
                            label={<FormattedMessage id="CreateElementsDrawer.width" />}
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={circle.width}
                            onChange={(e) => setCircle({ ...circle, width: e.target.value })}
                            onWheel={(e) => handleWheelInput(e, circle.width, (v) => setCircle({ ...circle, width: v }), wheelConfig)}
                            {...numberInputProps}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2">{unitType === EUnitType.METRIC ? mmLocalizedLabel : inLocalizedLabel}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={8}>
                          <TextField
                            label={<FormattedMessage id="CreateElementsDrawer.length" />}
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={circle.length}
                            onChange={(e) => setCircle({ ...circle, length: e.target.value })}
                            onWheel={(e) => handleWheelInput(e, circle.length, (v) => setCircle({ ...circle, length: v }), wheelConfig)}
                            {...numberInputProps}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2">{unitType === EUnitType.METRIC ? mmLocalizedLabel : inLocalizedLabel}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body1" fontWeight="bold" color="#9B9B9B">
                    <FormattedMessage id="CreateElementsDrawer.circle" />
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* TRIANGLE */}
          <Grid item>
            {/* 1. BUTTON & INPUTS ROW */}
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <ImsDoubleRectButton
                  icon={<IconElementTriangle />}
                  selected={selectedShape === EElementType.TRIANGLE}
                  onClick={() => handleShapeSelection(EElementType.TRIANGLE)}
                />
              </Grid>

              <Grid item xs>
                {selectedShape === EElementType.TRIANGLE ? (
                  <Grid container spacing={1} direction="column">
                    <Grid item>
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={8}>
                          <TextField
                            // FIXED: Added FormattedMessage
                            label={<FormattedMessage id="CreateElementsDrawer.triangle.sideA" defaultMessage="Seite A" />}
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={triangle.sideA}
                            onChange={(e) => updateTriangle('sideA', e.target.value)}
                            onWheel={(e) => handleWheelInput(e, triangle.sideA, (v) => updateTriangle('sideA', v), wheelConfig)}
                            {...numberInputProps}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Box component="span" sx={iconWrapperSx}>
                                    <IconTriangleSideA />
                                  </Box>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2">{unitType === EUnitType.METRIC ? mmLocalizedLabel : inLocalizedLabel}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={8}>
                          <TextField
                            // FIXED: Added FormattedMessage
                            label={<FormattedMessage id="CreateElementsDrawer.triangle.sideB" defaultMessage="Seite B" />}
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={triangle.sideB}
                            onChange={(e) => updateTriangle('sideB', e.target.value)}
                            onWheel={(e) => handleWheelInput(e, triangle.sideB, (v) => updateTriangle('sideB', v), wheelConfig)}
                            {...numberInputProps}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Box component="span" sx={iconWrapperSx}>
                                    <IconTriangleSideB />
                                  </Box>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2">{unitType === EUnitType.METRIC ? mmLocalizedLabel : inLocalizedLabel}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={8}>
                          <TextField
                            // FIXED: Added FormattedMessage
                            label={<FormattedMessage id="CreateElementsDrawer.triangle.sideC" defaultMessage="Seite C" />}
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={triangle.sideC}
                            onChange={(e) => updateTriangle('sideC', e.target.value)}
                            onWheel={(e) => handleWheelInput(e, triangle.sideC, (v) => updateTriangle('sideC', v), wheelConfig)}
                            {...numberInputProps}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Box component="span" sx={iconWrapperSx}>
                                    <IconTriangleSideC />
                                  </Box>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="body2">{unitType === EUnitType.METRIC ? mmLocalizedLabel : inLocalizedLabel}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body1" fontWeight="bold" color="#9B9B9B">
                    <FormattedMessage id="CreateElementsDrawer.triangle" defaultMessage="Triangle" />
                  </Typography>
                )}
              </Grid>
            </Grid>
            {/* 2. DESCRIPTION ROW */}
            {selectedShape === EElementType.TRIANGLE && (
              <Grid container spacing={2} sx={{ mt: 1 }} alignItems="center">
              {/* LEFT: Icon */}
              <Grid item sx={{ width: 80 + 16, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                  <Box 
                    sx={{ 
                      width: 50,   
                      height: 50,  
                      color: '#9B9B9B', 
                      opacity: 0.5,
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      '& svg': {
                        width: '100%',
                        height: '100%',
                        fontSize: 'inherit' 
                      }
                    }}
                  >
                    <IconTriangleAngle />
                  </Box>
                </Grid>

                {/* RIGHT: Text */}
                <Grid item xs sx={{ pr: 3 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.3, display: 'block' }}>
                    <FormattedMessage 
                      id="CreateElementsDrawer.triangle.description.autoCalculation" 
                      defaultMessage="Wird für Seite C kein Wert eingetragen, wird sie <b>automatisch aus A und B bei 90°</b> berechnet."
                      values={{
                        b: (chunks: React.ReactNode) => <Box component="span" fontWeight="bold">{chunks}</Box>
                      }}
                    />
                  </Typography>
                  <Box height={6} />
                  <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.3, display: 'block' }}>
                    <FormattedMessage 
                      id="CreateElementsDrawer.triangle.description.manualChange" 
                      defaultMessage="Wird C manuell geändert, verändert sich dadurch auch der Winkel zwischen A und B."
                    />
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Grid>

          {error && (
            <Grid item>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Grid>
          )}
        </Grid>
        {selectedShape && (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" sx={{ backgroundColor: immersightBlue, color: '#fff', '&:hover': { backgroundColor: '#1565c0' } }} onClick={handleInsert}>
              <FormattedMessage id="CreateElementsDrawer.insert" />
            </Button>
          </Box>
        )}
      </Grid>
    </Drawer>
  );
};

export default CreateElementsDrawer;